import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { Ams } from 'src/app/amsconfig';
import { Fx } from 'src/app/core/dto';
import {
  putAction,
  delAction,
  initializeAction,
  applyBatchAction,
  BatchChange,
} from './clientData.actions';
import {
  signalrConnectedAction,
  signalrDisconnectedAction,
  signalrReconnectedAction,
  initSystemInfoAction,
} from './store/misc/actions';
import { getPdfAuthToken } from './auth-interceptor.service';

@Injectable({
  providedIn: 'root',
})
export class ClientDataHubService {
  connection;
  connectionId: string = '';

  // (audit U9) Restart backoff: the old fixed 5 s retry hammered a server that was still down.
  private restartDelayMs = ClientDataHubService.MIN_RESTART_DELAY_MS;

  private static readonly MIN_RESTART_DELAY_MS = 5000;
  private static readonly MAX_RESTART_DELAY_MS = 60000;

  /**
   * Collections whose puts and deletes arrive in bursts: one message per job when a big schedule
   * change syncs back. Their messages are held for `BATCH_WINDOW_MS` and applied as one
   * `applyBatchAction`, instead of one store update - and one scheduler grid rebuild - per message.
   * Only collections nothing listens to message-by-message belong here (SolutionTree, for one,
   * has listeners on its individual PUT/DEL actions in patterns-api.service.ts).
   */
  static readonly BATCHED_COLLECTIONS = new Set([
    'AvailableJob',
    'JobSummary',
    'JobDetail',
    'Schedule',
    'MachineSchedule',
  ]);
  static readonly BATCH_WINDOW_MS = 100;

  private pendingChanges = new Map<string, BatchChange[]>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private store: Store) {
    const server = Ams.Config.BASE_URL;
    // In PDF-generation mode the headless browser has no session cookie, so we
    // pass the JWT as an access_token query param. SignalR uses the same URL for
    // both the negotiate POST and the WebSocket upgrade.
    const pdfToken = getPdfAuthToken();
    const hubUrl = pdfToken
      ? `${server}/hubs?access_token=${encodeURIComponent(pdfToken)}`
      : `${server}/hubs`;
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.on('objectPut', (collection, data) => {
      if (ClientDataHubService.BATCHED_COLLECTIONS.has(collection)) {
        this.queueChange(collection, { type: 'put', payload: data });
        return;
      }
      this.store.dispatch(putAction({ collection, payload: data }));
    });

    this.connection.on('objectDelete', (collection, id) => {
      if (ClientDataHubService.BATCHED_COLLECTIONS.has(collection)) {
        this.queueChange(collection, { type: 'del', id });
        return;
      }
      this.store.dispatch(delAction({ collection, id }));
    });

    this.connection.on('objectBatchPut', (collection, batch) => {
      this.store.dispatch(initializeAction({ collection, payload: batch }));
    });

    // Server calls this when we connect. Put the data in the store.
    this.connection.on('sendSystemInfo', (systemInfo) => {
      this.store.dispatch(initSystemInfoAction({ payload: systemInfo }));
    });

    this.connection.onclose((error) => {
      console.log('ClientDataHub: disconnected', error);
      this.store.dispatch(signalrDisconnectedAction());
      this.start(); // the built in reconnect timed out. Now try to restart.
    });

    this.connection.onreconnecting((error) => {
      console.log(`ClientDataHub: reconnecting`);
      // (audit U9) Mark the store disconnected for the duration of SignalR's automatic reconnect.
      // Without this, isSignalRConnected stays true and SubscriptionService.fireWhenConnected
      // invokes hub methods on a connection that cannot send them, so the calls are dropped.
      this.store.dispatch(signalrDisconnectedAction());
    });

    this.connection.onreconnected((error) => {
      console.log(`ClientDataHub: reconnected === ${this.connection.connectionId}`, error);

      // (audit U9) Dispatch unconditionally, even when the connection id is unchanged: onreconnecting
      // now marks the store disconnected, so skipping this would leave it disconnected forever. The
      // replay effect is idempotent - the server dedupes by subscription key - so replaying is safe.
      this.connectionId = this.connection.connectionId;
      this.store.dispatch(signalrReconnectedAction({ payload: this.connection.connectionId }));
    });

    // Start the hub connection
    this.start();
  }

  private queueChange(collection: string, change: BatchChange) {
    let changes = this.pendingChanges.get(collection);
    if (!changes) {
      changes = [];
      this.pendingChanges.set(collection, changes);
    }
    changes.push(change);
    if (this.flushTimer === null) {
      this.flushTimer = setTimeout(() => this.flushChanges(), ClientDataHubService.BATCH_WINDOW_MS);
    }
  }

  /** Applies everything queued, one action per collection, in arrival order within each. */
  flushChanges() {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    const pending = this.pendingChanges;
    this.pendingChanges = new Map();
    pending.forEach((changes, collection) => {
      this.store.dispatch(applyBatchAction({ collection, changes }));
    });
  }

  start() {
    console.log('ClientDataHub: connecting');
    this.connection
      .start()
      .then(() => {
        console.log(`ClientDataHub: connected === ${this.connection.connectionId}`);
        // A restart after onclose is a reconnect as far as subscriptions are concerned: the server
        // no longer has any for the previous connection id, so the live list must be re-sent.
        const isRestart = this.connectionId !== '';
        this.connectionId = this.connection.connectionId;
        // (audit U9) Back to the floor delay so the next outage starts retrying promptly.
        this.restartDelayMs = ClientDataHubService.MIN_RESTART_DELAY_MS;
        this.store.dispatch(
          isRestart
            ? signalrReconnectedAction({ payload: this.connection.connectionId })
            : signalrConnectedAction({ payload: this.connection.connectionId })
        );
      })
      .catch((error) => {
        console.error('ClientDataHub: error', error);
        // (audit U9) Exponential backoff, 5 s -> 60 s, instead of a fixed 5 s retry forever.
        const delay = this.restartDelayMs;
        this.restartDelayMs = Math.min(delay * 2, ClientDataHubService.MAX_RESTART_DELAY_MS);
        setTimeout(() => this.start(), delay);
      });
  }

  addSubscription(sub: Fx.Subscription) {
    const newSub = cloneDeep(sub);
    if (this.connection.state === 'Connected') {
      // connected
      // The server side json binding won't bind a number[] to a string[].
      // So at the last possible place, we'll manually map.
      if (newSub.filterDef.type === Fx.IN) {
        let filterDef = newSub.filterDef as Fx.In;
        filterDef.values = filterDef.values.map((x) => x.toString());
      }
      this.connection
        .invoke('AddSubscription', newSub)
        .catch((error) => console.error('AddSubscription call failed', sub, error));
    } else {
      console.log(
        `ClientDataHub: not connected connection.state: ${this.connection.state}, unable to subscribe to ${sub.collection}`
      );
    }
  }
  deleteSubscription(sub: Fx.Subscription) {
    if (this.connection.state === 'Connected') {
      // connected
      console.log(`ClientDataHub (${this.connection.connectionId}) : deleteSubscription`, sub);
      this.connection
        .invoke('DeleteSubscription', sub)
        .catch((error) => console.error('DeleteSubscription call failed', sub, error));
    } else {
      console.log(
        'not connected',
        'connection.state: ',
        this.connection.state,
        'unable to delete subscription to ' + sub.collection
      );
    }
  }
}
